import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, UserPlusIcon, StethoscopeIcon, LineChartIcon, FolderOpenIcon, FileCheckIcon, BriefcaseIcon } from 'lucide-react';
const modules = [{
  num: '1',
  icon: UserPlusIcon,
  title: 'Primera Consulta e Historia Clínica',
  tagline: 'Porque sin un registro sólido desde el primer día, todo el tratamiento queda en el aire',
  bullets: ['Ficha de admisión completa del paciente nuevo', 'Historia clínica kinésica con anamnesis detallada', 'Consentimiento informado listo para firmar']
}, {
  num: '2',
  icon: StethoscopeIcon,
  title: 'Evaluación y Diagnóstico Kinésico',
  tagline: 'Medí, objetivá y documentá el estado real de tu paciente antes de tocar nada',
  bullets: ['Planillas de goniometría y rango articular', 'Test muscular y escala de dolor EVA con mapa corporal', 'Evaluación postural y tests funcionales']
}, {
  num: '3',
  icon: LineChartIcon,
  title: 'Evolución y Seguimiento de Sesiones',
  tagline: 'Lo que no se registra, no existió — ni para vos, ni para la obra social, ni legalmente',
  bullets: ['Hoja de sesión con técnicas aplicadas y respuesta del paciente', 'Registro de objetivos por etapa de tratamiento', 'Control de progreso semana a semana']
}, {
  num: '4',
  icon: FolderOpenIcon,
  title: 'Planillas por Área de Tratamiento',
  tagline: 'Formularios específicos para las patologías que más vas a ver en tu consultorio',
  bullets: ['Planillas para columna, rodilla, hombro y cadera', 'Registro para lesiones deportivas y patología neurológica', 'Formulario específico para kinesiología respiratoria']
}, {
  num: '5',
  icon: FileCheckIcon,
  title: 'Informes Profesionales y Obras Sociales',
  tagline: 'Los documentos formales que necesitás para cobrar, derivar y cerrar tratamientos',
  bullets: ['Informe inicial, de evolución y de alta en formato presentable', 'Solicitud de sesiones para obras sociales', 'Plantilla de derivación a otro especialista']
}, {
  num: '6',
  icon: BriefcaseIcon,
  title: 'Gestión del Consultorio',
  tagline: 'Todo lo administrativo que nadie te enseñó en la carrera pero que necesitás desde el día uno',
  bullets: ['Registro y base de datos de pacientes activos', 'Control de turnos y seguimiento de pagos', 'Planilla de alta y baja de paciente']
}];
export function Modules() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-14 md:py-24 bg-slate-50" data-id="element-193">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-194">
        <div className="text-center mb-10 md:mb-14" data-id="element-195">
          <span className="inline-block text-brand-accent font-bold text-xs uppercase tracking-wider mb-3" data-id="element-196">
            6 Tomos Profesionales
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-197">
            Qué incluye el Megapack
          </h2>
          <div className="w-20 h-1 bg-brand-accent mx-auto rounded-full" data-id="element-198"></div>
          <p className="text-slate-500 mt-4 text-sm md:text-base" data-id="element-199">
            Tocá cada tomo para ver el contenido completo
          </p>
        </div>

        <div className="space-y-3" data-id="element-200">
          {modules.map((mod, index) => {
          const isOpen = openIndex === index;
          const Icon = mod.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: index * 0.05
          }} className={`bg-white rounded-2xl border overflow-hidden transition-all duration-300 ${isOpen ? 'border-brand-accent shadow-lg shadow-brand-accent/10' : 'border-slate-200 shadow-sm hover:border-slate-300'}`} data-id="element-201">
                <button onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full text-left p-4 md:p-5 flex items-center gap-3 md:gap-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 rounded-2xl" aria-expanded={isOpen} data-id="element-202">
                  {/* Number badge with icon */}
                  <div className={`flex-shrink-0 relative w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-brand-accent' : 'bg-slate-900'}`} data-id="element-203">
                    <Icon className={`w-5 h-5 md:w-6 md:h-6 transition-colors ${isOpen ? 'text-white' : 'text-brand-accent'}`} data-id="element-204" />
                    <span className={`absolute -top-1.5 -right-1.5 w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center text-[10px] md:text-xs font-black border-2 border-white transition-colors ${isOpen ? 'bg-white text-brand-accent' : 'bg-brand-accent text-white'}`} data-id="element-205">
                      {mod.num}
                    </span>
                  </div>

                  {/* Title */}
                  <div className="flex-grow min-w-0" data-id="element-206">
                    <div className="text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-207">
                      Tomo {mod.num}
                    </div>
                    <h3 className="text-sm md:text-lg font-bold text-slate-900 leading-snug" data-id="element-208">
                      {mod.title}
                    </h3>
                  </div>

                  {/* Chevron */}
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-brand-accent text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-209">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-210" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-211">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-212">
                      <div className="px-4 md:px-5 pb-5 md:pb-6 pl-[76px] md:pl-[100px]" data-id="element-213">
                        <p className="text-xs md:text-sm italic text-slate-500 mb-4 border-l-2 border-brand-accent pl-3" data-id="element-214">
                          "{mod.tagline}"
                        </p>
                        <ul className="space-y-2.5" data-id="element-215">
                          {mod.bullets.map((bullet, i) => <motion.li key={i} initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: i * 0.08
                    }} className="flex items-start gap-2.5" data-id="element-216">
                              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-accent/10 flex items-center justify-center mt-0.5" data-id="element-217">
                                <CheckIcon className="w-3 h-3 text-brand-accent stroke-[3]" data-id="element-218" />
                              </div>
                              <span className="text-slate-700 text-sm leading-relaxed" data-id="element-219">
                                {bullet}
                              </span>
                            </motion.li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mt-8 text-center" data-id="element-220">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-xs md:text-sm text-slate-600 shadow-sm" data-id="element-221">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-222"></span>
            <span className="font-medium" data-id="element-223">
              Planillas listas para imprimir y usar desde el día uno
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}